import { Component, HostBinding, OnDestroy, OnInit, TemplateRef, ViewChild } from '@angular/core';
import { Router } from '@angular/router';
import { ApplicationsService } from 'app/applications/applications.service';
import { ServiceType } from 'app/core/service-type.model';
import { isNil } from 'app/core/validation';
import { DocumentsComponent } from 'app/documents/documents.component';
import { BillResponseToCamelCaseObj, ConsumptionService } from 'app/services/consumption.service';
import { Limits, LimitsService } from 'app/services/limits.service';
import { RoutingService } from 'app/services/routing.service';
import { DialogEventEnum } from 'app/ui/dialog/dialog-event.enum';
import { UIDialogService } from 'app/ui/dialog/dialog.service';
import { UISnackBarService } from 'app/ui/snack-bar/snack-bar.service';
import { UserSettingsService } from 'app/user/user-settings.service';
import { UserService } from 'app/user/user.service';
import { forkJoin, Observable, Subscription, timer } from 'rxjs';
import { switchMap, tap } from 'rxjs/operators';
import { SidebarSettingsService } from './sidebar-settings.service';

@Component({
  selector: 'sidebar',
  templateUrl: 'sidebar.component.html',
  styleUrls: ['sidebar.component.scss']
})
export class SidebarComponent implements OnInit, OnDestroy {
  private _activatedRouteSubscription: Subscription;
  private _subscription: Subscription;
  private _contactSubscription: Subscription;
  activeDocumentsComponent: boolean;
  activeServiceType: ServiceType | null = null;
  demoApplications: string;
  customApplicationsPages: number;
  customApplicationsDocuments: number;
  consumptionPercentage?: number;
  developersSectionIsHidden: boolean = true;
  serviceTypeSubscription: Subscription;
  documentsTypes = [
    {
      title: 'Invoices',
      type: 'invoices',
      serviceType: ServiceType.InvoiceCapture,
      isExpanded: false
    },
    {
      title: 'Other Documents',
      type: 'documents',
      serviceType: ServiceType.DocumentCapture,
      isExpanded: false
    }
  ];
  limit: number;
  pageCount: number = 0;
  documentCount: number = 0;

  @ViewChild('contact')
  private _contactTemplate: TemplateRef<any>;

  expandDevelopersSection() {
    this.developersSectionIsHidden = !this.developersSectionIsHidden;
  }

  @HostBinding('class.active')
  get visible(): boolean {
    return this._sidebarSettingsService.visible;
  }

  set visible(value: boolean) {
    this._sidebarSettingsService.visible = value;
  }

  get serviceType(): ServiceType {
    return this._route.url.indexOf('documents') === -1
        ? ServiceType.InvoiceCapture
        : ServiceType.DocumentCapture;
  }

  constructor(
    private _applicationsService: ApplicationsService,
    private _routingService: RoutingService,
    private _consumptionService: ConsumptionService,
    private _limitsService: LimitsService,
    private _route: Router,
    private _dialogService: UIDialogService,
    private _sidebarSettingsService: SidebarSettingsService,
    private _snackBarService: UISnackBarService,
    private _userService: UserService,
    private _userSettingsService: UserSettingsService
  ) { }

  ngOnInit(): void {
    const { url } = this._route.routerState.snapshot;

    this.serviceTypeSubscription = this._userSettingsService.serviceType$.subscribe(
      () => {
        const showDevelopersSection = url.split('documents/').length > 1 || url.split('invoices/').length > 1;
        if (this.activeServiceType !== this.serviceType && !showDevelopersSection) {
          this.developersSectionIsHidden = true;
        } else {
          this.developersSectionIsHidden = false;
        }

        this.activeServiceType = this.serviceType;
      }
    );
    this._subscription = this._userSettingsService.serviceType$
      .pipe(
        tap(
          () => (this.demoApplications = null),
          (this.consumptionPercentage = null)
        ),
        switchMap(() => timer(0, 7000)),
        switchMap(() => this._getConsumption()),
      )
      .subscribe(([limits, bill]) => {
        const itemsLimit = this._getItemsLimit(limits);
        this.demoApplications = this._getDemoApplicationsString(
          itemsLimit,
          bill
        );
        this.consumptionPercentage = this._getConsumptionPercentage(
          itemsLimit,
          bill
        );

        if ([ServiceType.DocumentCapture, ServiceType.InvoiceCapture].includes(this.serviceType)) {
          this.pageCount = bill.demo.pageCount;
          this.documentCount = bill.demo.documentCount;
        }
        this.limit = itemsLimit;
        this.customApplicationsPages = bill.paid.pageCount;
        this.customApplicationsDocuments = bill.paid.documentCount;
      });

    this._contactSubscription = this._dialogService.dialogEvents.subscribe(
      (event: DialogEventEnum) => {
        if (event === DialogEventEnum.save) {
          this._snackBarService.open(this._contactTemplate);
        }
      }
    );

    this._activatedRouteSubscription = this._routingService.routes.subscribe(route => {
      this.activeDocumentsComponent = route.component === DocumentsComponent;
    });
  }

  showCustomApplicationBalanceReport() {
    return this._applicationsService
      .applications(this.serviceType)
      .filter(app => app.isCustomized).length;
  }

  private _getItemsLimit(limits: Limits): number {
    if (this.serviceType === 'dc') {
      return limits.dc.pagesCount;
    } else {
      return limits.ic.pagesCount;
    }
  }

  private _getDemoApplicationsString(
    itemsLimit: number,
    bill: BillResponseToCamelCaseObj
  ): string {
    return `${bill.demo.pageCount} of ${itemsLimit} trial pages`;
  }

  private _getConsumptionPercentage(
    itemsLimit: number,
    bill: BillResponseToCamelCaseObj
  ) {
    return Math.round((100 * bill.demo.pageCount) / itemsLimit);
  }

  private _getConsumption(): Observable<[Limits, BillResponseToCamelCaseObj]> {
    return forkJoin([
      this._limitsService.getLimit(),
      this._consumptionService.getConsumption(this.serviceType),
    ]);
  }

  showProgressBar(): boolean {
    return !isNil(this.consumptionPercentage);
  }

  showStatistics(document) {
    if (this.isUsageReportPage()) {
        return false
    }

    return this.demoApplications
        && this.serviceType === document.serviceType
        && this.showProgressBar()
        && !this._applicationsService.defaultApplication().isCustomized;
  }

  ngOnDestroy(): void {
    if (this._subscription) {
      this._subscription.unsubscribe();
    }

    this._contactSubscription.unsubscribe();
    this._activatedRouteSubscription.unsubscribe();
  }

  toggle(): void {
    this.visible = !this.visible;
  }

  userIsAdmin(): boolean {
    return this._userService.isAdmin();
  }

  userIsServiceAdmin(): boolean {
    return this._userService.isUserServiceAdmin();
  }

  showDevelopersSettings(serviceType: ServiceType): boolean {
    switch (serviceType) {
      case 'ic': {
        return /invoices/.test(this._route.url);
      }
      case 'dc': {
        return /documents/.test(this._route.url);
      }
      default: {
        return false
      }
    }
  }

  private isUsageReportPage(): boolean {
    return !/documents|invoices/.test(this._route.url);
  }
}
